import React, { useState } from "react";
import { Helmet } from "react-helmet";
import HeaderBottom from "../components/HomePage/HeaderBottom";
import "./SupportPage.css";

const issueOptions = [
  "Chatbot not responding",
  "Wrong answers",
  "Integration issue",
  "Other",
];

const initialForm = {
  fullName: "",
  businessName: "",
  email: "",
  issueType: issueOptions[0],
  description: "",
};

const supportApiBaseUrl =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://ai-automation-audit-01-e0855711738d.herokuapp.com";

const SupportPage = () => {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState({
    type: "",
    message: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitState({ type: "", message: "" });

    try {
      const response = await fetch(
        `${supportApiBaseUrl}/support-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Something went wrong.");
      }

      const emailNotificationFailed =
        result.emailNotificationStatus &&
        result.emailNotificationStatus !== "sent";

      setSubmitState(
        emailNotificationFailed
          ? {
              type: "warning",
              message:
                "Your support request was saved successfully, but the email notification could not be sent yet.",
            }
          : {
              type: "success",
              message: "Thanks, your support request has been sent successfully.",
            }
      );
      setFormData(initialForm);
    } catch (error) {
      setSubmitState({
        type: "error",
        message:
          error.message ||
          "We could not send your request right now. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Support | AuraWeb</title>
        <meta
          name="description"
          content="Report an issue with your AI assistant and our team will review it quickly."
        />
        <link rel="canonical" href="https://auraweb.click/support" />
      </Helmet>

      <div className="boxed_wrapper ltr">
        <HeaderBottom />

        <section className="support-page-section">
          <div className="container">
            <div className="support-page-hero">
              <div className="row align-items-center g-4">
                <div className="col-lg-6">
                  <span className="support-page-eyebrow">Support</span>
                  <h1>Need help with your AI assistant?</h1>
                  <p>
                    Experiencing an issue with your AI assistant? Let us know and
                    we&apos;ll fix it fast.
                  </p>
                  <ul className="support-page-points">
                    <li>Fast issue triage</li>
                    <li>Clear status updates</li>
                    <li>Handled by the AuraWeb team</li>
                  </ul>
                </div>

                <div className="col-lg-6">
                  <div className="support-page-summary">
                    <div className="support-page-summary-card">
                      <strong>What to include</strong>
                      <span>
                        Tell us what&apos;s happening, which business it affects, and
                        what result you expected.
                      </span>
                    </div>
                    <div className="support-page-summary-card">
                      <strong>Notification email</strong>
                      <span>support@auraweb.click receives the request details.</span>
                    </div>
                    <div className="support-page-summary-card">
                      <strong>Common issues</strong>
                      <span>Chatbot replies, incorrect answers, and integration problems.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="support-form-shell">
              <div className="row g-4 align-items-start">
                <div className="col-lg-4">
                  <div className="support-form-sidecard">
                    <h2>Submit a support request</h2>
                    <p>
                      Complete the form and we&apos;ll notify the support team right
                      away.
                    </p>
                    <div className="support-form-sidegrid">
                      {issueOptions.map((option) => (
                        <span key={option}>{option}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="col-lg-8">
                  <div className="support-form-card">
                    <form onSubmit={handleSubmit}>
                      <div className="row clearfix">
                        <div className="col-md-6 col-sm-12 form-group">
                          <label htmlFor="support-full-name">Full name</label>
                          <input
                            id="support-full-name"
                            type="text"
                            name="fullName"
                            placeholder="Your full name"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 form-group">
                          <label htmlFor="support-business-name">Business name</label>
                          <input
                            id="support-business-name"
                            type="text"
                            name="businessName"
                            placeholder="Business name"
                            value={formData.businessName}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 form-group">
                          <label htmlFor="support-email">Email address</label>
                          <input
                            id="support-email"
                            type="email"
                            name="email"
                            placeholder="you@business.com"
                            value={formData.email}
                            onChange={handleChange}
                            required
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 form-group">
                          <label htmlFor="support-issue-type">Type of issue</label>
                          <select
                            id="support-issue-type"
                            name="issueType"
                            value={formData.issueType}
                            onChange={handleChange}
                            required
                          >
                            {issueOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-lg-12 col-md-12 col-sm-12 form-group">
                          <label htmlFor="support-description">Description of the issue</label>
                          <textarea
                            id="support-description"
                            name="description"
                            placeholder="Describe the issue in as much detail as possible."
                            value={formData.description}
                            onChange={handleChange}
                            rows="7"
                            required
                          />
                        </div>

                        {submitState.message ? (
                          <div className="col-lg-12">
                            <div
                              className={`support-form-alert ${submitState.type}`}
                              role="status"
                            >
                              {submitState.message}
                            </div>
                          </div>
                        ) : null}

                        <div className="col-lg-12 col-md-12 col-sm-12 form-group message-btn">
                          <button
                            className="theme-btn btn-one"
                            type="submit"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Sending..." : "Submit"}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SupportPage;
